import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetAdPackagesQuery, useDeleteAdPackageMutation, useToggleAdPackageStatusMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import DateRangePicker from '../../../components/DateRangePicker';
import ActionButtons from '../../../components/ActionButtons';
import Pagination from '../../../components/Pagination';
import { showSuccess, showError } from '../../../utils/toast';

// Format date as "d M, Y" (e.g., "27 Mar, 2023")
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const AdminAdPackages: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [daterange, setDaterange] = useState('');
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch } = useGetAdPackagesQuery({ search, daterange, page });
  const [deleteAdPackage] = useDeleteAdPackageMutation();
  const [toggleAdPackageStatus] = useToggleAdPackageStatusMutation();

  // Reset to page 1 when search or daterange changes
  useEffect(() => {
    setPage(1);
  }, [search, daterange]);

  const handleToggleStatus = async (id: number, currentStatus: boolean) => {
    try {
      const newStatus = currentStatus ? false : true;
      const result = await toggleAdPackageStatus({ id, status: newStatus }).unwrap();
      showSuccess(result.message || 'Ad Package status updated successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update ad package status. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const result = await deleteAdPackage(id).unwrap();
      showSuccess(result.message || 'Ad Package deleted successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to delete ad package. Please try again.');
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return <div className="alert alert-danger m-5">Error loading ad packages</div>;
  }

  const packages = data?.data || { data: [], current_page: 1, last_page: 1 };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Ad Packages</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Ad Packages</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <form onSubmit={(e) => { e.preventDefault(); }}>
                    <div className="d-flex align-items-center position-relative my-1">
                      <span className="svg-icon svg-icon-1 position-absolute ms-6">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"></rect>
                          <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"></path>
                        </svg>
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-solid w-350px ps-15 me-5"
                        placeholder="Search Ad Package Title.."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <div className="position-relative d-flex align-items-center">
                        <DateRangePicker
                          value={daterange}
                          onChange={setDaterange}
                          placeholder="Date Range"
                        />
                        {(search || daterange) && (
                          <a href="#" onClick={(e) => { e.preventDefault(); setSearch(''); setDaterange(''); }} className="btn btn-sm btn-light-primary me-3">Clear</a>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
                <div className="card-toolbar">
                  <a href="#" className="btn btn-base" onClick={(e) => { e.preventDefault(); navigate('/admin/ad-packages/add'); }}>
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect opacity="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="currentColor"></rect>
                        <rect x="4.36396" y="11.364" width="16" height="2" rx="1" fill="currentColor"></rect>
                      </svg>
                    </span>
                    Add New Ad Package
                  </a>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th>#</th>
                        <th>TITLE</th>
                        <th>TITLE ARABIC</th>
                        <th>DURATION</th>
                        <th>FREE DAYS</th>
                        <th>AMOUNT</th>
                        <th>DAILY RATE</th>
                        <th>BENEFITS COUNT</th>
                        <th>RECOMMENDED</th>
                        <th>STATUS</th>
                        <th>CREATED DATE</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {packages.data.length === 0 ? (
                        <tr>
                          <td colSpan={12} className="text-center py-10">
                            <span className="text-muted">No ad packages found</span>
                          </td>
                        </tr>
                      ) : (
                        packages.data.map((pkg: any, index: number) => (
                          <tr key={pkg.id}>
                            <td>{(packages.current_page - 1) * 15 + index + 1}</td>
                            <td>{pkg.title || 'N/A'}</td>
                            <td>{pkg.title_ar || 'N/A'}</td>
                            <td>{pkg.duration} days</td>
                            <td>{pkg.free_days || 0} days</td>
                            <td>${pkg.amount || '0.00'}</td>
                            <td>${pkg.daily_rate ? parseFloat(pkg.daily_rate).toFixed(2) : '0.00'}/day</td>
                            <td>{Array.isArray(pkg.benefits) ? pkg.benefits.length : 0}</td>
                            <td>
                              {pkg.is_recommended ? (
                                <span className="badge badge-light-success">Recommended</span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={pkg.status === true}
                                  onChange={() => handleToggleStatus(pkg.id, pkg.status)}
                                  style={{ cursor: 'pointer' }}
                                />
                              </div>
                            </td>
                            <td>{formatDate(pkg.created_at)}</td>
                            <td className="text-center">
                              <ActionButtons
                                onEdit={() => navigate(`/admin/ad-packages/${pkg.id}/edit`)}
                                onDelete={() => handleDelete(pkg.id)}
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {packages.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={packages.current_page}
                      lastPage={packages.last_page}
                      total={packages.total || 0}
                      perPage={packages.per_page || 15}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAdPackages;

